<script setup>
import { ref, computed, h, useTemplateRef, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useOperators } from 'dashboard/components-next/filter/operators';
import { useStore } from 'dashboard/composables/store';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import {
  generateAutomationPayload,
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES, AUTOMATIONS } from './constants';
import {
  conditionAttributeOptions,
  conditionSchema,
  getConditionOperatorOptions,
  statusOptions,
  priorityOptions,
  actionStatusOptions,
} from '../dealAutomation/dealAutomationOptions';
import { DEAL_AUTOMATION_RULE_EVENTS, isDealAutomationEvent } from './dealAutomationBridge';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: { type: Object, required: true },
  getConditionDropdownValues: { type: Function, required: true },
  getActionDropdownValues: { type: Function, required: true },
  appendNewCondition: { type: Function, required: true },
  appendNewAction: { type: Function, required: true },
  removeFilter: { type: Function, required: true },
  removeAction: { type: Function, required: true },
  resetAction: { type: Function, required: true },
  onEventChange: { type: Function, required: true },
});

const emit = defineEmits(['save']);
const automation = defineModel('automation', { type: Object, default: null });

const DEAL_ACTION_DEFS = [
  { key: 'deal_create', label: 'Criar negócio', inputType: 'search_select' },
  { key: 'deal_assign_agent', label: 'Atribuir responsável', inputType: 'search_select' },
  { key: 'deal_change_stage', label: 'Mover para estágio', inputType: 'search_select' },
  { key: 'deal_change_status', label: 'Alterar status', inputType: 'search_select' },
  { key: 'deal_change_priority', label: 'Alterar prioridade', inputType: 'search_select' },
  { key: 'deal_mark_as_lost', label: 'Marcar como perdido', inputType: 'search_select' },
  { key: 'deal_add_note', label: 'Adicionar anotação', inputType: 'textarea' },
  { key: 'deal_create_activity', label: 'Criar atividade', inputType: 'search_select' },
  { key: 'deal_send_webhook_event', label: 'Enviar webhook', inputType: 'url' },
];

const DEAL_INPUT_TYPE_MAP = {
  select_pipeline: 'searchSelect',
  select_stage: 'searchSelect',
  select_status: 'searchSelect',
  select_priority: 'searchSelect',
  select_assignee: 'searchSelect',
  select_loss_reason: 'searchSelect',
  number: 'plainText',
  datetime: 'date',
  text: 'plainText',
};

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { operators } = useOperators();
const store = useStore();

const dialogRef = ref(null);
const conditionsRef = useTemplateRef('conditionsRef');
const errors = ref({});

const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const lossReasons = computed(() => store.getters['dealLossReasons/getLossReasons'] || []);

const pipelineOptions = computed(() =>
  pipelines.value.map(p => ({ id: p.id, name: p.name || `Pipeline #${p.id}` }))
);
const stageOptions = computed(() =>
  pipelines.value.flatMap(p =>
    (p.stages || []).map(s => ({
      id: s.id,
      name: `${p.name || `Pipeline #${p.id}`} / ${s.name || `Estágio #${s.id}`}`,
    }))
  )
);
const assigneeOptions = computed(() =>
  agents.value.map(a => ({ id: a.id, name: a.name || a.email || `Usuário #${a.id}` }))
);
const lossReasonOptions = computed(() =>
  lossReasons.value.map(r => ({ id: r.id, name: r.name || `Motivo #${r.id}` }))
);

// ── Pipeline-Stage dual-select ──────────────────────────────────────────────
const PIPELINE_STAGE_ACTIONS = new Set(['deal_create', 'deal_change_stage']);
const isPipelineStageAction = name => PIPELINE_STAGE_ACTIONS.has(String(name || ''));

const actionPipelineMap = ref({});

const getStagesForPipelineId = pipelineId => {
  const p = pipelines.value.find(pl => pl.id === Number(pipelineId));
  return (p?.stages || []).map(s => ({ id: s.id, name: s.name || `Estágio #${s.id}` }));
};

const getActionStageId = i => {
  const params = automation.value?.actions?.[i]?.action_params;
  const val = Array.isArray(params) ? params[0] : params;
  return val ?? null;
};


const onPipelineSelect = (i, rawVal) => {
  const pipelineId = rawVal ? Number(rawVal) : null;
  actionPipelineMap.value = { ...actionPipelineMap.value, [i]: pipelineId };
  if (automation.value?.actions?.[i]) {
    automation.value.actions[i] = { ...automation.value.actions[i], action_params: [] };
  }
};

const setActionStageId = (i, rawVal) => {
  const stageId = Number(rawVal) || null;
  if (automation.value?.actions?.[i]) {
    automation.value.actions[i] = {
      ...automation.value.actions[i],
      action_params: stageId ? [stageId] : [],
    };
  }
};

const onDealActionNameChange = (i, newName) => {
  const newMap = { ...actionPipelineMap.value };
  delete newMap[i];
  actionPipelineMap.value = newMap;
  if (automation.value?.actions?.[i]) {
    automation.value.actions[i] = { action_name: newName, action_params: [] };
  }
};

const getActionErrorMessage = i => {
  const key = errors.value[`action_${i}`];
  return key ? t(`AUTOMATION.ERRORS.${key}`) : '';
};

const inferPipelinesFromActions = () => {
  const newMap = {};
  (automation.value?.actions || []).forEach((action, i) => {
    if (!isPipelineStageAction(action.action_name)) return;
    const params = action.action_params || [];
    const stageId = Array.isArray(params) ? params[0] : null;
    if (!stageId) return;
    const match = pipelines.value.find(p =>
      (p.stages || []).some(s => String(s.id) === String(stageId))
    );
    if (match) newMap[i] = match.id;
  });
  actionPipelineMap.value = newMap;
};
// ────────────────────────────────────────────────────────────────────────────

const getDealConditionOptions = rawKey => {
  if (rawKey === 'pipeline_id') return pipelineOptions.value;
  if (rawKey === 'pipeline_stage_id') return stageOptions.value;
  if (rawKey === 'status') return statusOptions.map(o => ({ id: o.value, name: o.label }));
  if (rawKey === 'priority') return priorityOptions.map(o => ({ id: o.value, name: o.label }));
  if (rawKey === 'assignee_id') return assigneeOptions.value;
  if (rawKey === 'deal_loss_reason_id') return lossReasonOptions.value;
  return [];
};

const getDealActionOptions = actionName => {
  const name = String(actionName || '');
  if (name === 'deal_create' || name === 'deal_change_stage') return stageOptions.value;
  if (name === 'deal_assign_agent') return assigneeOptions.value;
  if (name === 'deal_change_status') return actionStatusOptions.map(o => ({ id: o.value, name: o.label }));
  if (name === 'deal_change_priority') return priorityOptions.map(o => ({ id: o.value, name: o.label }));
  if (name === 'deal_mark_as_lost') return lossReasonOptions.value;
  return [];
};

const isEditMode = computed(() => props.mode === 'edit');

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const cancelKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT' : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const isDealEvent = computed(() => isDealAutomationEvent(automation.value?.event_name));

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(attribute.key);
    return {
      ...attribute,
      name: skipTranslation ? attribute.name : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const dealFilterTypes = computed(() =>
  conditionAttributeOptions.map(option => {
    const schema = conditionSchema[option.value];
    const inputType = DEAL_INPUT_TYPE_MAP[schema?.inputType] || 'plainText';
    const options = getDealConditionOptions(option.value);
    const rawOperators = getConditionOperatorOptions(option.value);
    const filterOperators = rawOperators.map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: !['is_present', 'is_not_present'].includes(op.value),
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });
    return {
      attributeKey: `deal_${option.value}`,
      value: `deal_${option.value}`,
      attributeName: option.label,
      label: option.label,
      inputType,
      options,
      filterOperators,
    };
  })
);

const filterTypes = computed(() => {
  if (isDealEvent.value) return dealFilterTypes.value;

  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);
  return attributes.map(attr => {
    if (attr.disabled) return { value: attr.key, label: attr.name, disabled: true };

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];
    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });
    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const automationRuleEvents = computed(() => [
  ...AUTOMATION_RULE_EVENTS.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  })),
  ...DEAL_AUTOMATION_RULE_EVENTS.map(event => ({ key: event.key, value: event.label })),
]);

const hasAutomationMutated = computed(() =>
  Boolean(
    automation.value?.conditions?.[0]?.values ||
    automation.value?.actions?.[0]?.action_params?.length
  )
);

const automationActionTypes = computed(() => {
  if (isDealEvent.value) {
    // Deal events: context-specific actions only (deal already exists — create would be a loop)
    return DEAL_ACTION_DEFS.filter(a => a.key !== 'deal_create');
  }

  // Non-deal events: respect per-event action whitelist from AUTOMATIONS constant
  const event = eventName.value;
  const allowedKeys = new Set((AUTOMATIONS[event]?.actions || []).map(a => a.key));
  const baseActions = AUTOMATION_ACTION_TYPES
    .filter(({ key }) => allowedKeys.has(key))
    .filter(({ key }) => key !== 'add_sla' || isCloudFeatureEnabled('sla'))
    .map(action => ({
      ...action,
      label: t(`AUTOMATION.ACTIONS.${action.label}`),
    }));

  // Universal deal action: create a deal from any event (conversation, message, comment)
  const universalDealActions = DEAL_ACTION_DEFS.filter(a => a.key === 'deal_create');

  return [...baseActions, ...universalDealActions];
});

// Helpers that convert raw IDs to {id,name} objects for SingleSelect.
// Must be declared after automationActionTypes to satisfy no-use-before-define.
const getActionNameModel = actionName => {
  const found = automationActionTypes.value.find(a => a.key === actionName);
  return found ? { id: found.key, name: found.label } : null;
};
const getPipelineModel = i =>
  pipelineOptions.value.find(p => p.id === actionPipelineMap.value[i]) || null;
const getStageModel = i => {
  const stageId = getActionStageId(i);
  if (!stageId && stageId !== 0) return null;
  return (
    getStagesForPipelineId(actionPipelineMap.value[i]).find(
      s => String(s.id) === String(stageId)
    ) || null
  );
};

const wrappedGetActionDropdownValues = actionName => {
  if (String(actionName || '').startsWith('deal_')) return getDealActionOptions(actionName);
  return props.getActionDropdownValues(actionName);
};

const hasConditionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('condition_'))
);
const hasActionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('action_'))
);

watch(
  () => automation.value,
  () => { if (Object.keys(errors.value).length) errors.value = {}; },
  { deep: true }
);

// Hydrate deal values (primitive → {id,name}) when store options load.
// Conditions are hydrated only for deal events (they always use deal_ prefix).
// Actions are always hydrated — deal_create can appear in non-deal events.
const hydrateDealValues = () => {
  if (!automation.value) return;
  let changed = false;

  const conditions = isDealEvent.value
    ? (automation.value.conditions || []).map(condition => {
        const key = String(condition.attribute_key || '');
        if (!key.startsWith('deal_')) return condition;
        const rawKey = key.replace(/^deal_/, '');
        const schema = conditionSchema[rawKey];
        if (!schema?.inputType?.startsWith('select_')) return condition;

        const existing = condition.values;
        if (existing && typeof existing === 'object' && !Array.isArray(existing) && existing.id !== undefined) {
          return condition;
        }
        const firstVal = Array.isArray(existing) ? existing[0] : existing;
        if (firstVal === undefined || firstVal === null || firstVal === '') return condition;

        const options = getDealConditionOptions(rawKey);
        const found = options.find(o => String(o.id) === String(firstVal));
        if (!found) return condition;

        changed = true;
        return { ...condition, values: { id: found.id, name: found.name } };
      })
    : (automation.value.conditions || []);

  const actions = (automation.value.actions || []).map(action => {
    const name = String(action.action_name || '');
    if (!name.startsWith('deal_')) return action;
    // Pipeline-stage actions use native <select> with plain IDs — no {id,name} hydration needed
    if (isPipelineStageAction(name)) return action;
    const actionDef = DEAL_ACTION_DEFS.find(d => d.key === name);
    if (actionDef?.inputType !== 'search_select') return action;

    const existing = action.action_params;
    if (existing && typeof existing === 'object' && !Array.isArray(existing) && existing.id !== undefined) {
      return action;
    }
    const firstVal = Array.isArray(existing) ? existing[0] : existing;
    if (firstVal === undefined || firstVal === null || firstVal === '') return action;

    const options = getDealActionOptions(name);
    const found = options.find(o => String(o.id) === String(firstVal));
    if (!found) return action;

    changed = true;
    return { ...action, action_params: { id: found.id, name: found.name } };
  });

  if (changed) automation.value = { ...automation.value, conditions, actions };
};

watch([pipelines, agents, lossReasons], () => {
  hydrateDealValues();
  inferPipelinesFromActions();
});
watch(() => automation.value?.event_name, () => hydrateDealValues());
// Rebuild pipeline map when actions are added/removed (indices shift on removal)
watch(() => automation.value?.actions?.length, () => {
  actionPipelineMap.value = {};
  nextTick(() => inferPipelinesFromActions());
});

const isConditionsValid = () => {
  if (!conditionsRef.value) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const resetValidation = () => {
  errors.value = {};
  conditionsRef.value?.forEach(c => c.resetValidation());
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(ft => ft.attributeKey === condition.attribute_key);
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard' ? '' : filterType?.attributeModel || '';
  });
};

const handleEventChange = () => {
  if (isDealEvent.value) {
    automation.value.conditions = [{
      attribute_key: 'deal_status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    }];
    automation.value.actions = [{ action_name: 'deal_change_stage', action_params: [] }];
    return;
  }
  props.onEventChange();
};

const handleAppendNewCondition = () => {
  if (isDealEvent.value) {
    automation.value.conditions.push({
      attribute_key: 'deal_status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    });
    return;
  }
  props.appendNewCondition();
};

const handleAppendNewAction = () => {
  if (isDealEvent.value) {
    automation.value.actions.push({ action_name: 'deal_change_stage', action_params: [] });
    return;
  }
  props.appendNewAction();
};

const open = () => { resetValidation(); dialogRef.value?.open(); };
const close = () => { resetValidation(); dialogRef.value?.close(); };

const emitSaveAutomation = () => {
  if (isDealEvent.value) {
    errors.value = {};
    if (!automation.value?.name?.trim()) {
      errors.value.name = 'NAME_REQUIRED';
      return;
    }
    const payload = generateAutomationPayload(automation.value);
    const dealPayload = {
      name: payload.name,
      description: payload.description,
      event_name: payload.event_name,
      active: payload.active ?? true,
      conditions: (payload.conditions || []).map((c, i, arr) => ({
        attribute_key: String(c.attribute_key).replace(/^deal_/, ''),
        filter_operator: c.filter_operator,
        values: c.values || [],
        query_operator: i < arr.length - 1 ? c.query_operator : null,
      })),
      actions: (payload.actions || []).map(a => ({
        action_name: String(a.action_name).replace(/^deal_/, ''),
        action_params: a.action_params || [],
      })),
    };
    emit('save', dealPayload, props.mode);
    return;
  }

  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    emit('save', payload, props.mode);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    :title="$t(titleKey)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
  >
    <div v-if="automation" class="flex flex-col w-full">
      <woot-input
        v-model="automation.name"
        :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
        type="text"
        :class="{ error: errors.name }"
        :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
        :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
      />
      <woot-input
        v-model="automation.description"
        :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
        type="text"
        :class="{ error: errors.description }"
        :error="errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''"
        :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
      />
      <div class="mb-6">
        <label :class="{ error: errors.event_name }">
          {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
          <select v-model="automation.event_name" class="m-0" @change="handleEventChange">
            <option
              v-for="event in automationRuleEvents"
              :key="event.key"
              :value="event.key"
            >
              {{ event.value }}
            </option>
          </select>
          <span v-if="errors.event_name" class="message">
            {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
          </span>
        </label>
        <p
          v-if="!isEditMode && hasAutomationMutated"
          class="text-xs text-right text-n-teal-10 pt-1"
        >
          {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
        </p>
      </div>

      <!-- Conditions -->
      <section class="mb-5">
        <label>{{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}</label>
        <ul
          class="grid gap-4 list-none p-3 mb-4 outline outline-1 rounded-xl -outline-offset-1"
          :class="
            hasConditionErrors
              ? 'outline-n-ruby-5 bg-n-ruby-2/50'
              : 'outline-n-weak dark:outline-n-strong'
          "
        >
          <template v-for="(condition, i) in automation.conditions" :key="i">
            <ConditionRow
              v-if="i === 0"
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(i)"
            />
            <ConditionRow
              v-else
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:query-operator="automation.conditions[i - 1].query_operator"
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              show-query-operator
              @remove="removeFilter(i)"
            />
          </template>
          <div>
            <NextButton
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
              @click="handleAppendNewCondition"
            />
          </div>
        </ul>
      </section>

      <!-- Actions -->
      <section>
        <label>{{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}</label>
        <ul
          class="grid list-none p-3 mb-4 outline outline-1 rounded-xl -outline-offset-1 border-solid"
          :class="
            hasActionErrors
              ? 'outline-n-ruby-5 bg-n-ruby-2/50'
              : 'outline-n-weak dark:outline-n-strong'
          "
        >
          <template v-for="(action, i) in automation.actions" :key="i">
            <!-- Pipeline + Stage dual-select (deal_create / deal_change_stage) -->
            <li v-if="isPipelineStageAction(action.action_name)" class="list-none py-2 first:pt-0 last:pb-0">
              <div
                class="flex items-center gap-2"
                :class="{ 'animate-wiggle': errors[`action_${i}`] }"
              >
                <SingleSelect
                  :model-value="getActionNameModel(action.action_name)"
                  :options="automationActionTypes.map(t => ({ id: t.key, name: t.label }))"
                  dropdown-max-height="max-h-[7.5rem]"
                  disable-deselect
                  class="flex-shrink-0"
                  @update:model-value="v => onDealActionNameChange(i, v?.id)"
                />
                <SingleSelect
                  :model-value="getPipelineModel(i)"
                  :options="pipelineOptions"
                  dropdown-max-height="max-h-[7.5rem]"
                  @update:model-value="v => onPipelineSelect(i, v?.id ?? null)"
                />
                <SingleSelect
                  :model-value="getStageModel(i)"
                  :options="getStagesForPipelineId(actionPipelineMap[i])"
                  dropdown-max-height="max-h-[7.5rem]"
                  :disabled="!actionPipelineMap[i]"
                  @update:model-value="v => setActionStageId(i, v?.id ?? null)"
                />
                <NextButton
                  sm
                  solid
                  slate
                  icon="i-lucide-trash"
                  class="flex-shrink-0"
                  @click="removeAction(i)"
                />
              </div>
              <span v-if="errors[`action_${i}`]" class="text-sm text-n-ruby-11">
                {{ getActionErrorMessage(i) }}
              </span>
            </li>
            <!-- Standard Chatwoot action input for everything else -->
            <AutomationActionInput
              v-else
              v-model="automation.actions[i]"
              :action-types="automationActionTypes"
              dropdown-max-height="max-h-[7.5rem]"
              :dropdown-values="wrappedGetActionDropdownValues(action.action_name)"
              :show-action-input="showActionInput(automationActionTypes, action.action_name)"
              :error-message="
                errors[`action_${i}`] ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`) : ''
              "
              :initial-file-name="isEditMode ? getFileName(action, automation.files) : ''"
              @reset-action="resetAction(i)"
              @remove-action="removeAction(i)"
            />
          </template>
          <div class="pt-2">
            <NextButton
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
              @click="handleAppendNewAction"
            />
          </div>
        </ul>
      </section>

      <div class="w-full mt-8">
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-4">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t(cancelKey)"
            @click.prevent="close"
          />
          <NextButton
            solid
            blue
            type="submit"
            :label="$t(submitKey)"
            @click="emitSaveAutomation"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
